// UPLOAD-NEVER-BLOCKS: the uploads in flight, on every screen — each file "Uploading… n%" with its
// bar while the bytes go out, then "Processing…" (its text is read in the background) with Open,
// or the reason it failed. Stored rows clear themselves after a while; a failed one stays until
// dismissed. Nothing shows when nothing is uploading.
import { Badge, Button, Progress } from "@companyos/ui";
import { Link } from "@tanstack/react-router";
import { FileTextIcon, XIcon } from "lucide-react";
import { useEffect } from "react";
import {
  dismissUploads,
  inFlight,
  retryUploadJob,
  type UploadJob,
  uploadPercent,
  useUploadJobs,
} from "./upload-queue";

const STORED_STAYS_MS = 8_000;

function Row({ job }: { job: UploadJob }) {
  const pct = uploadPercent(job);
  useEffect(() => {
    if (job.state !== "stored") return;
    const t = setTimeout(() => dismissUploads(job.key), STORED_STAYS_MS);
    return () => clearTimeout(t);
  }, [job.state, job.key]);
  return (
    <li
      className="flex flex-col gap-1 px-3 py-2"
      data-slot="upload-tray-row"
      data-state={job.state}
    >
      <span className="flex items-center gap-2 text-sm">
        <FileTextIcon aria-hidden="true" className="size-4 shrink-0 text-faint" />
        <span className="min-w-0 flex-1 truncate">{job.page?.title ?? job.name}</span>
        {job.state === "failed" ? (
          <>
            <Badge variant="danger">Upload failed</Badge>
            <Button
              variant="outline"
              size="xs"
              data-slot="upload-tray-retry"
              onClick={() => retryUploadJob(job.key)}
            >
              Retry
            </Button>
          </>
        ) : job.state === "stored" ? (
          <Badge variant="success" data-slot="upload-tray-uploaded">
            Uploaded 100%
          </Badge>
        ) : (
          <Badge variant="neutral" data-slot="upload-tray-uploading">
            {job.state === "queued" ? "Waiting…" : `Uploading… ${pct}%`}
          </Badge>
        )}
        {job.state === "stored" && job.page ? (
          <Link
            to="/c/$slug/work/p/$pageId"
            params={{ slug: job.slug, pageId: job.page.id }}
            className="shrink-0 text-xs font-medium text-primary hover:underline"
          >
            Open
          </Link>
        ) : null}
        {job.state === "stored" || job.state === "failed" ? (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Dismiss ${job.name}`}
            title="Dismiss"
            onClick={() => dismissUploads(job.key)}
          >
            <XIcon aria-hidden="true" />
          </Button>
        ) : null}
      </span>
      {job.state === "uploading" || job.state === "queued" ? (
        <Progress value={pct} aria-label={`Upload of ${job.name}`} />
      ) : null}
      {job.error ? <span className="text-xs text-danger-text">{job.error}</span> : null}
      {job.unshared ? (
        <span className="text-xs text-warning-text">Not shared: {job.unshared}</span>
      ) : null}
    </li>
  );
}

export function UploadTray() {
  const jobs = useUploadJobs();
  if (!jobs.length) return null;
  const going = inFlight(jobs);
  return (
    <section
      aria-label="Uploads"
      data-slot="upload-tray"
      className="fixed bottom-4 left-18 z-40 w-80 max-w-[calc(100vw-5.5rem)] overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-lg"
    >
      <header className="flex items-center gap-2 border-b border-border px-3 py-2 text-sm font-medium">
        {going ? "Uploading" : "Uploads"}
        <span className="text-xs font-normal text-muted-foreground">
          {going ? "Keep this tab open until the files are up" : "Processing shows in Docs"}
        </span>
      </header>
      <ul className="m-0 flex max-h-72 list-none flex-col divide-y divide-border overflow-y-auto p-0">
        {jobs.map((j) => (
          <Row key={j.key} job={j} />
        ))}
      </ul>
    </section>
  );
}
